// =============================================================================
// BP-07-03: useSavedResources Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSavedResources,
  saveResource,
  unsaveResource,
  updateSavedResource,
  getSavedResourceFolders,
} from '@/features/resources/api/resourcesApi';
import type {
  SavedResource,
  Resource,
  PaginationParams,
  SaveResourceRequest,
  PaginatedResponse,
} from '@/features/resources/types/resourcesTypes';
import { resourceQueryKeys } from '@/features/resources/types/resourcesTypes';

/**
 * Hook for fetching user's saved resources
 */
export function useSavedResources(pagination: PaginationParams = {}) {
  return useQuery({
    queryKey: [...resourceQueryKeys.saved(), pagination],
    queryFn: () => getSavedResources(pagination),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for fetching saved resource folders
 */
export function useSavedResourceFolders() {
  return useQuery({
    queryKey: [...resourceQueryKeys.saved(), 'folders'],
    queryFn: getSavedResourceFolders,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for saving a resource (with optimistic update)
 */
export function useSaveResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resourceId,
      request = {},
    }: {
      resourceId: string;
      request?: SaveResourceRequest;
    }) => saveResource(resourceId, request),
    
    // Optimistic update
    onMutate: async ({ resourceId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: resourceQueryKeys.saved() });
      await queryClient.cancelQueries({ queryKey: resourceQueryKeys.details() });

      // Snapshot the previous value
      const previousSaved = queryClient.getQueryData(resourceQueryKeys.saved());

      // Optimistically update resource's isSaved flag
      queryClient.setQueriesData(
        { queryKey: resourceQueryKeys.details() },
        (old: Resource | undefined) => {
          if (old && old.id === resourceId) {
            return { ...old, isSaved: true, saveCount: old.saveCount + 1 };
          }
          return old;
        }
      );

      return { previousSaved };
    },
    
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousSaved) {
        queryClient.setQueryData(resourceQueryKeys.saved(), context.previousSaved);
      }
      
      // Revert isSaved flag
      queryClient.setQueriesData(
        { queryKey: resourceQueryKeys.details() },
        (old: Resource | undefined) => {
          if (old && old.id === variables.resourceId) {
            return { ...old, isSaved: false, saveCount: old.saveCount - 1 };
          }
          return old;
        }
      );
    },
    
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.saved() });
    },
  });
}

/**
 * Hook for unsaving a resource (with optimistic update)
 */
export function useUnsaveResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resourceId: string) => unsaveResource(resourceId),
    
    onMutate: async (resourceId) => {
      await queryClient.cancelQueries({ queryKey: resourceQueryKeys.saved() });

      const previousSaved = queryClient.getQueryData<PaginatedResponse<SavedResource>>(
        resourceQueryKeys.saved()
      );

      // Optimistically remove from saved list
      if (previousSaved) {
        queryClient.setQueryData(resourceQueryKeys.saved(), {
          ...previousSaved,
          items: previousSaved.items.filter((item) => item.resourceId !== resourceId),
          total: previousSaved.total - 1,
        });
      }

      // Update isSaved flag on resource
      queryClient.setQueriesData(
        { queryKey: resourceQueryKeys.details() },
        (old: Resource | undefined) => {
          if (old && old.id === resourceId) {
            return { ...old, isSaved: false, saveCount: Math.max(0, old.saveCount - 1) };
          }
          return old;
        }
      );

      return { previousSaved };
    },
    
    onError: (err, resourceId, context) => {
      if (context?.previousSaved) {
        queryClient.setQueryData(resourceQueryKeys.saved(), context.previousSaved);
      }
      
      queryClient.setQueriesData(
        { queryKey: resourceQueryKeys.details() },
        (old: Resource | undefined) => {
          if (old && old.id === resourceId) {
            return { ...old, isSaved: true, saveCount: old.saveCount + 1 };
          }
          return old;
        }
      );
    },
    
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.saved() });
    },
  });
}

/**
 * Hook for updating saved resource (notes, folder)
 */
export function useUpdateSavedResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      savedResourceId,
      request,
    }: {
      savedResourceId: string;
      request: SaveResourceRequest;
    }) => updateSavedResource(savedResourceId, request),
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.saved() });
    },
  });
}

/**
 * Hook to check if a resource is saved
 */
export function useIsResourceSaved(resourceId: string): boolean {
  const { data } = useSavedResources();
  return data?.items.some((item) => item.resourceId === resourceId) ?? false;
}

/**
 * Combined hook for save/unsave toggle
 */
export function useToggleSaveResource() {
  const save = useSaveResource();
  const unsave = useUnsaveResource();

  return {
    toggle: (resourceId: string, isSaved: boolean) => {
      if (isSaved) {
        return unsave.mutateAsync(resourceId);
      } else {
        return save.mutateAsync({ resourceId });
      }
    },
    isLoading: save.isPending || unsave.isPending,
  };
}

export default useSavedResources;

